import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { DrawerLink } from '../../app/DrawerLink';
import { Character } from '../character/Character';
import { fromSeed, type Character as CharacterConfig } from '../character/config';
import { api } from '../../lib/api';
import { subscribeToStation } from '../../lib/realtime';

/**
 * The people in the room, drawn rather than counted.
 *
 * ## Why a number was not enough
 *
 * "5 listening" is the least evocative way to say a room has people in it. It
 * reads as a statistic about a broadcast; faces read as a place with somebody
 * in it, and the difference is whether a host going live to four people feels
 * like a failure or like four people turned up.
 *
 * ## Everyone is counted, not everyone is drawn
 *
 * Anonymous listeners have nobody to show, and signed-in ones can switch
 * themselves out of the crowd - listening was invisible until this existed, and
 * quietly outing somebody who tuned into a set they would rather not be seen at
 * is the one way this feature could do harm. Both still count toward the total,
 * so the trailing number is the rest of the audience rather than an overflow.
 *
 * ## It keeps its own clock
 *
 * The room's state arrives over a socket and changes when the broadcast does.
 * The crowd changes when somebody walks in, which is a different rhythm and a
 * much noisier one - a heartbeat every thirty seconds per listener. So it polls
 * on its own rather than riding a channel that would then re-send the whole
 * room every time one person's heartbeat landed.
 */
/**
 * How wide one character is drawn.
 *
 * The figure is 220 by 272, so this is the width and the height follows - about
 * seventy pixels tall, which is where the drawing stops being a silhouette and
 * you can tell a beanie from a cap. Much smaller and everybody looks the same,
 * which defeats the point of anybody having made one.
 */
const CHARACTER_WIDTH = 56;

/**
 * And bigger where there is room for it.
 *
 * The constant above is the small end - the size the drawing renders at, which
 * this overrides upward on wider screens. A crowd is the one thing on this
 * screen that gets better with size: at a desk you can read what people are
 * wearing, which is the whole reason anybody bothered making a character.
 *
 * `h-auto` is doing real work. The drawing sets both width and height as
 * attributes, so overriding only the width would squash everybody.
 */
const CHARACTER_SIZES = 'h-auto w-[56px] sm:w-[68px] xl:w-[84px] 2xl:w-[96px]';

interface Face {
  username: string;
  display_name: string;
  avatar: string | null;
  /** Their own character, once they have made one. Seeded from the name until then. */
  character: CharacterConfig | null;
  since: string;
}

/** What somebody in the crowd can be seen doing, and the word on the button. */
const REACTIONS: [string, string][] = [
  ['clap', '👏'],
  ['dance', '🕺'],
  ['wave', '👋'],
  ['hooray', '🙌'],
  ['pump', '✊'],
  ['lighter', '🔥'],
];

export function Crowd({
  username,
  stationId,
  listening,
}: {
  username: string;
  stationId: number;
  /** Reacting is something you do because you are in the room - see `ReactAction`. */
  listening: boolean;
}) {
  /*
   * Who is doing what, right now.
   *
   * Held here and nowhere else: a reaction is a moment with no row behind it,
   * so there is nothing to reconcile with and nothing to reload. Each one
   * clears itself on a timer the server set, which keeps the two ends agreeing
   * about how long a wave lasts without the client deciding.
   */
  const [reacting, setReacting] = useState<Record<string, string>>({});

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    const stop = subscribeToStation(stationId, (event, payload) => {
      if (event !== 'station.react') return;

      const { username: who, reaction, seconds } = (payload ?? {}) as {
        username?: string;
        reaction?: string;
        seconds?: number;
      };

      if (!who || !reaction) return;

      setReacting((current) => ({ ...current, [who]: reaction }));

      timers.push(
        setTimeout(
          () =>
            setReacting((current) => {
              // Only if they have not started something else since - clearing
              // blindly would cut a second reaction short.
              if (current[who] !== reaction) return current;

              const { [who]: _gone, ...rest } = current;

              return rest;
            }),
          (seconds ?? 6) * 1000,
        ),
      );
    });

    return () => {
      stop?.();
      timers.forEach(clearTimeout);
    };
  }, [stationId]);

  const { data } = useQuery({
    queryKey: ['crowd', username],
    queryFn: () =>
      api.get<{ faces: Face[]; total: number; hidden: number }>(
        `/users/${encodeURIComponent(username)}/live/crowd`,
      ),
    /*
     * Twenty seconds, against a thirty-second heartbeat. Faster would report
     * arrivals the server has not heard about yet; much slower and somebody who
     * left is still standing there.
     */
    refetchInterval: 20_000,
    // The room keeps playing in a background tab, and so does its audience.
    refetchIntervalInBackground: true,
  });

  const react = useMutation({
    mutationFn: (reaction: string) =>
      api.post(`/users/${encodeURIComponent(username)}/live/react`, { reaction }),
    /*
     * Nothing on success. The reaction comes back over the room's own channel
     * like everybody else's, so the person who pressed it sees exactly what the
     * room sees - and a local shortcut would be the one copy that could differ.
     */
  });

  const faces = data?.faces ?? [];

  // Nobody in yet. A heading over an empty strip is furniture; the listener
  // count above already says the room is quiet.
  if (!data || data.total === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      {/*
        The controls, on their own line above the crowd.

        They used to sit inside the row with the characters, which is fine with
        six people in a room and wrong with twenty-six: `flex-wrap` gave them a
        place in the queue, so they ended up somewhere in the middle of the
        audience and pushed whoever came after them onto another line. A row
        that grows and a row of buttons are different things and should not
        share a wrap.

        Only for somebody actually listening: the server refuses a reaction from
        outside the room, and a button that exists to be refused is worse than
        one that is not there.
      */}
      {listening && (
        <div className="flex flex-wrap items-center gap-1" data-reactions>
          {REACTIONS.map(([reaction, glyph]) => (
            <button
              key={reaction}
              type="button"
              title={reaction}
              aria-label={reaction}
              data-react={reaction}
              onClick={() => void react.mutate(reaction)}
              disabled={react.isPending}
              className="rounded-[var(--radius-pill)] border border-line px-2.5 py-1 text-[14px] leading-none transition-colors hover:border-line-strong hover:bg-raised disabled:opacity-40 xl:px-3 xl:py-1.5 xl:text-[16px]"
            >
              {glyph}
            </button>
          ))}
        </div>
      )}

      <div
        /*
         * Aligned to the bottom, so everybody is standing on the same floor
         * whatever they are wearing on their head. Centred, they would each
         * hover at a different height.
         */
        className="flex flex-wrap items-end gap-x-1 gap-y-3"
        data-crowd={data.total}
      >
      {faces.map((face, index) => (
        <DrawerLink
          key={face.username}
          to={`/${face.username}`}
          data-crowd-face={face.username}
          title={face.display_name || face.username}
          className="group relative"
        >
          {/*
            The whole person, standing.

            Not a cropped head in a circle: this is an audience, and an audience
            is people standing in a room. The drawing carries its own contact
            shadow, so a row of them reads as a floor rather than as a strip of
            cut-outs - which is the entire difference between a crowd and a list
            of avatars.

            No ring and no disc around it either. Both are avatar furniture, and
            a figure in a bordered box stops looking like somebody in the room.
          */}
          <Character
            config={face.character ?? fromSeed(face.username)}
            size={CHARACTER_WIDTH}
            crop="full"
            anim={reacting[face.username] ?? 'idle'}
            /*
             * Two offsets across the row, so a crowd breathing in unison looks
             * like an audience rather than a chorus line. Taken from the
             * position rather than the name: neighbours must differ, and two
             * people with similar names standing apart need not.
             */
            stagger={index % 3}
            className={`${CHARACTER_SIZES} transition-transform group-hover:-translate-y-1`}
          />
        </DrawerLink>
      ))}

      {data.hidden > 0 && (
        <span
          data-crowd-hidden={data.hidden}
          /*
           * Not "+3 more faces we ran out of room for" - these are listeners
           * with nobody to draw, and saying "more listening" is the honest
           * version of a crowd that is smaller than the room.
           */
          title={`${data.hidden} listening without a face shown`}
          className="mb-2 ml-1.5 font-mono text-[11px] text-faint"
        >
          +{data.hidden} listening
        </span>
      )}
      </div>
    </div>
  );
}
